"use client";

import { useEffect } from "react";
import { useCookieConsent } from "./CookieConsentProvider";

export const TRUSTED_SHOPS_SCRIPT_ID = "gs-consented-trusted-shops";
export const TRUSTED_SHOPS_ID = "X453AF47ABC8A6263A22A8E666E7617D8";

declare global {
  interface Window {
    _tsConfig?: Record<string, unknown>;
  }
}

interface ConsentAwareTrustedShopsProps {
  // Hides the floating corner badge while keeping order reporting and
  // review-invite emails working — those read the checkout div, not the
  // badge. Used in the customer portal, where the public-site badge isn't
  // wanted, only the order-reporting side effect.
  hideBadge?: boolean;
  // Removes and re-injects the script instead of skipping when one is
  // already present. The widget only scans the checkout div once, when its
  // script (re)loads — needed wherever the same component can represent a
  // new, distinct order each time it mounts (pair with a `key` on the order
  // id so the effect actually re-fires), e.g. a customer placing several
  // orders back to back in one portal session.
  forceReload?: boolean;
}

export default function ConsentAwareTrustedShops({
  hideBadge = false,
  forceReload = false,
}: ConsentAwareTrustedShopsProps) {
  const { choice, ready } = useCookieConsent();

  useEffect(() => {
    if (!ready || choice !== "all") {
      return;
    }

    const existingScript = document.getElementById(TRUSTED_SHOPS_SCRIPT_ID);
    if (existingScript) {
      if (!forceReload) {
        return;
      }
      existingScript.remove();
    }

    window._tsConfig = {
      yOffset: "100",
      variant: "reviews",
      customElementId: "trustedShopsCheckout",
      trustcardDirection: "",
      customBadgeWidth: "",
      customBadgeHeight: "",
      disableResponsive: "false",
      disableTrustbadge: hideBadge ? "true" : "false",
    };

    const script = document.createElement("script");
    script.id = TRUSTED_SHOPS_SCRIPT_ID;
    script.type = "text/javascript";
    script.charset = "utf-8";
    script.async = true;
    script.src = `https://widgets.trustedshops.com/js/${TRUSTED_SHOPS_ID}.js`;
    document.head.appendChild(script);
  }, [choice, ready, hideBadge, forceReload]);

  return null;
}
